import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"

export default class extends Controller {
  static values = {
    url: String,
    enabled: { type: Boolean, default: true },
    interval: { type: Number, default: 10000 },
    renderedAt: String
  }

  connect() {
    this.failures = 0
    this.lastFetchedAt = 0
    this.animations = new Map()
    this.liveSnapshot = this.snapshotFromDom()
    this.lastPayloadGeneratedAt = Date.parse(this.renderedAtValue) || Date.now()
    this.toastQueue = []
    this.toastVisible = false
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.visibilityChanged = this.visibilityChanged.bind(this)
    this.connectionChanged = this.connectionChanged.bind(this)
    this.pauseForNavigation = this.pauseForNavigation.bind(this)
    document.addEventListener("visibilitychange", this.visibilityChanged)
    document.addEventListener("turbo:before-visit", this.pauseForNavigation)
    window.addEventListener("online", this.connectionChanged)
    window.addEventListener("offline", this.connectionChanged)

    // The server-rendered page is already current; avoid an immediate duplicate query.
    if (this.enabledValue) this.schedule(this.intervalValue)
  }

  disconnect() {
    this.stopTimer()
    this.abortController?.abort()
    this.animations.forEach((frame) => cancelAnimationFrame(frame))
    document.removeEventListener("visibilitychange", this.visibilityChanged)
    document.removeEventListener("turbo:before-visit", this.pauseForNavigation)
    window.removeEventListener("online", this.connectionChanged)
    window.removeEventListener("offline", this.connectionChanged)
  }

  async refresh() {
    if (!this.enabledValue || document.hidden || !navigator.onLine) {
      this.updateStatus(document.hidden ? "Paused" : "Offline")
      return
    }

    this.abortController?.abort()
    this.abortController = new AbortController()

    try {
      const response = await fetch(this.urlValue, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        cache: "no-store",
        signal: this.abortController.signal
      })
      if (!response.ok) throw new Error(`Dashboard refresh returned ${response.status}`)

      const payload = await response.json()
      if (!this.sameCalendarDates(payload)) {
        window.location.reload()
        return
      }

      const notifications = this.activityNotifications(payload)
      this.render(payload)
      this.queueActivityToasts(notifications)
      this.failures = 0
      this.lastFetchedAt = Date.now()
      this.updateStatus("Live")
      this.schedule(this.intervalValue)
    } catch (error) {
      if (error.name === "AbortError") return

      this.failures += 1
      this.updateStatus("Reconnecting")
      this.schedule(Math.min(this.intervalValue * (2 ** this.failures), 60000))
    }
  }

  render(payload) {
    const chartRows = payload.chart.rows

    Object.entries(payload.kpis).forEach(([key, metric]) => {
      this.element.querySelectorAll(`[data-live-value-key="${key}"], [data-live-chart-total="${key}"]`).forEach((element) => {
        this.animateValue(element, metric.value, metric.kind)
      })
      this.updateDelta(key, metric.comparison)
      this.updateSpark(key, metric, chartRows)
    })

    this.updateFundsChart(chartRows)
    this.updateContributionChart(chartRows)
    this.updateGlance(payload.glance)
    this.updateWeekly(payload.weekly)
  }

  snapshotFromDom() {
    const valueFor = (key) => {
      const element = this.element.querySelector(`[data-live-value-key="${key}"]`)
      if (!element) return 0
      return element.dataset.liveValue === undefined ? this.numberFromText(element.textContent) : Number(element.dataset.liveValue)
    }
    return {
      fundsRaised: valueFor("funds_raised"),
      contributions: valueFor("contributions"),
      partiesBooked: valueFor("parties_booked"),
      partiesClosed: valueFor("parties_closed")
    }
  }

  snapshotFromPayload(payload) {
    return {
      fundsRaised: Number(payload.kpis.funds_raised.value || 0),
      contributions: Number(payload.kpis.contributions.value || 0),
      partiesBooked: Number(payload.kpis.parties_booked.value || 0),
      partiesClosed: Number(payload.kpis.parties_closed.value || 0)
    }
  }

  activityNotifications(payload) {
    const generatedAt = Date.parse(payload.generated_at)
    if (!Number.isFinite(generatedAt) || generatedAt <= this.lastPayloadGeneratedAt) return []

    const previous = this.liveSnapshot
    const current = this.snapshotFromPayload(payload)
    this.liveSnapshot = current
    this.lastPayloadGeneratedAt = generatedAt

    const contributionCount = current.contributions - previous.contributions
    const contributionAmount = current.fundsRaised - previous.fundsRaised
    const closedCount = current.partiesClosed - previous.partiesClosed
    const createdCount = current.partiesBooked - previous.partiesBooked
    const notifications = []

    if (contributionCount > 0) {
      const amount = contributionAmount > 0 ? ` totalling ${this.format(contributionAmount, "money")}` : ""
      notifications.push({
        icon: "success",
        title: contributionCount === 1
          ? (contributionAmount > 0 ? `A ${this.format(contributionAmount, "money")} contribution just came in` : "A new contribution just came in")
          : `${this.format(contributionCount, "number")} new contributions${amount} just came in`
      })
    } else if (contributionAmount > 0) {
      notifications.push({
        icon: "success",
        title: `Funds raised increased by ${this.format(contributionAmount, "money")}`
      })
    }

    if (createdCount > 0) {
      notifications.push({
        icon: "info",
        title: createdCount === 1 ? "A new party was created" : `${this.format(createdCount, "number")} new parties were created`
      })
    }

    if (closedCount > 0) {
      notifications.push({
        icon: "success",
        title: closedCount === 1 ? "A party just closed" : `${this.format(closedCount, "number")} parties just closed`
      })
    }

    const anyMetricChanged = [contributionCount, contributionAmount, closedCount, createdCount].some((change) => Math.abs(change) > 0.001)
    if (!notifications.length && anyMetricChanged) {
      notifications.push({ icon: "info", title: "Dashboard totals were updated" })
    }

    return notifications
  }

  queueActivityToasts(notifications) {
    if (!notifications.length) return

    this.toastQueue.push(...notifications)
    this.showNextActivityToast()
  }

  showNextActivityToast() {
    if (this.toastVisible || !this.toastQueue.length) return

    this.toastVisible = true
    const notification = this.toastQueue.shift()
    Swal.fire({
      toast: true,
      position: "bottom-end",
      icon: notification.icon,
      title: notification.title,
      timer: 4500,
      timerProgressBar: true,
      showConfirmButton: false,
      customClass: {
        popup: "swal2-ea-popup",
        title: "swal2-ea-title"
      },
      didOpen: (toast) => {
        toast.addEventListener("mouseenter", Swal.stopTimer)
        toast.addEventListener("mouseleave", Swal.resumeTimer)
      }
    }).finally(() => {
      this.toastVisible = false
      this.showNextActivityToast()
    })
  }

  updateDelta(key, comparison) {
    const wrapper = this.element.querySelector(`[data-live-delta-key="${key}"]`)
    if (!wrapper) return

    const badge = document.createElement("span")
    badge.className = "metric-delta"
    if (comparison === null || comparison === undefined) {
      badge.classList.add("neutral")
      badge.textContent = "No comparison"
    } else {
      const icon = document.createElement("span")
      icon.className = "material-symbols-rounded"
      icon.textContent = comparison < 0 ? "south_east" : "north_east"
      badge.classList.add(comparison < 0 ? "down" : "up")
      badge.append(icon, document.createTextNode(`${Math.abs(comparison).toFixed(1).replace(/\.0$/, "")}%`))
    }
    wrapper.replaceChildren(badge)
  }

  updateSpark(key, metric, rows) {
    const chart = this.element.querySelector(`[data-live-series-key="${key}"]`)
    if (!chart) return

    const bars = Array.from(chart.querySelectorAll("[data-live-series-index]"))
    if (bars.length !== metric.series.length) return

    const maximum = Math.max(...metric.series.map(Number), 1)
    bars.forEach((bar, index) => {
      const value = Number(metric.series[index])
      bar.style.height = `${Math.max(Math.round((value / maximum) * 100), 8)}%`
      bar.dataset.tooltip = `${rows[index].tooltip} · ${this.format(value, metric.kind)} ${chart.dataset.liveSeriesLabel}`
    })
  }

  updateFundsChart(rows) {
    const bars = Array.from(this.element.querySelectorAll("[data-live-funds-bar]"))
    if (bars.length !== rows.length) return

    const maximum = Math.max(...rows.map((row) => Number(row.raised)), 1)
    bars.forEach((bar, index) => {
      const value = Number(rows[index].raised)
      bar.style.height = `${Math.max(Math.round((value / maximum) * 100), 2)}%`
      bar.dataset.tooltip = `${rows[index].tooltip} · ${this.format(value, "money")}`
    })
  }

  updateContributionChart(rows) {
    const line = this.element.querySelector("[data-live-contribution-line]")
    const area = this.element.querySelector("[data-live-contribution-area]")
    const points = Array.from(this.element.querySelectorAll("[data-live-contribution-point]"))
    if (!line || !area || points.length !== rows.length) return

    const maximum = Math.max(...rows.map((row) => Number(row.contributions)), 1)
    const coordinates = rows.map((row, index) => {
      const x = rows.length === 1 ? 160 : index * (320 / (rows.length - 1))
      const y = 130 - ((Number(row.contributions) / maximum) * 105)
      return [Number(x.toFixed(1)), Number(y.toFixed(1))]
    })
    const polyline = coordinates.map((point) => point.join(",")).join(" ")
    line.setAttribute("points", polyline)
    area.setAttribute("points", `0,130 ${polyline} 320,130`)

    points.forEach((group, index) => {
      const [x, y] = coordinates[index]
      group.querySelectorAll("circle").forEach((circle) => {
        circle.setAttribute("cx", x)
        circle.setAttribute("cy", y)
      })
      group.querySelector(".admin-chart-point-hit").dataset.tooltip = `${rows[index].tooltip} · ${this.format(rows[index].contributions, "number")} contributions`
    })
  }

  updateGlance(glance) {
    const explanation = this.element.querySelector("[data-live-glance-explanation]")
    if (explanation) explanation.title = glance.comparison_label
    Object.entries(glance.metrics).forEach(([key, metric]) => {
      const card = this.element.querySelector(`[data-live-glance-key="${key}"]`)
      if (!card) return
      const kind = key === "funds" ? "money" : "number"
      this.animateValue(card.querySelector("[data-live-glance-today]"), metric.today, kind)
      const maximum = Math.max(metric.today, metric.yesterday, 1)
      for (const day of ["today", "yesterday"]) {
        this.animateValue(card.querySelector(`[data-live-glance-total="${day}"]`), metric[day], kind)
        card.querySelector(`[data-live-glance-bar="${day}"]`).style.width = `${(metric[day] / maximum) * 100}%`
      }
      card.querySelector("[data-live-glance-countries]").textContent = Object.entries(metric.countries)
        .map(([country, value]) => `${country} ${this.format(value, kind)}`).join(" · ")

      const delta = metric.comparison
      const badge = card.querySelector("[data-live-glance-delta]")
      badge.className = `metric-delta ${delta === null || delta === 0 ? "neutral" : (delta < 0 ? "down" : "up")}`
      badge.title = delta === null ? `No same-time comparison: yesterday had no activity by this time. ${glance.comparison_label}` : glance.comparison_label
      const icon = badge.querySelector("[data-live-glance-delta-icon]")
      icon.hidden = delta === null || delta === 0
      icon.textContent = delta < 0 ? "trending_down" : "trending_up"
      badge.querySelector("[data-live-glance-delta-label]").textContent = delta === null ? "—" : `${delta > 0 ? "+" : ""}${new Intl.NumberFormat("en-CA", { maximumFractionDigits: 1 }).format(delta)}%`
    })
  }

  updateWeekly(weekly) {
    weekly.rows.forEach((row) => {
      const element = this.element.querySelector(`[data-live-week-date="${row.date}"]`)
      if (element) this.updateFields(element, row)
    })

    const total = this.element.querySelector("[data-live-week-total]")
    if (total) this.updateFields(total, weekly.total)
  }

  updateFields(row, values) {
    row.querySelectorAll("[data-live-field]").forEach((cell) => {
      const field = cell.dataset.liveField
      const kind = ["fees", "funds", "average"].includes(field) ? "money" : "number"
      this.animateValue(cell.querySelector("strong") || cell, values[field], kind)
    })
  }

  animateValue(element, nextValue, kind) {
    const next = Number(nextValue || 0)
    const current = element.dataset.liveValue === undefined ? this.numberFromText(element.textContent) : Number(element.dataset.liveValue)
    element.dataset.liveValue = next
    if (Math.abs(current - next) < 0.001) return

    const previousFrame = this.animations.get(element)
    if (previousFrame) cancelAnimationFrame(previousFrame)
    element.classList.add("is-live-changing")

    if (this.reducedMotion) {
      element.textContent = this.format(next, kind)
      this.finishAnimation(element)
      return
    }

    const startedAt = performance.now()
    const duration = 650
    const step = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1)
      const eased = 1 - ((1 - progress) ** 3)
      element.textContent = this.format(current + ((next - current) * eased), kind)
      if (progress < 1) {
        this.animations.set(element, requestAnimationFrame(step))
      } else {
        this.finishAnimation(element)
      }
    }
    this.animations.set(element, requestAnimationFrame(step))
  }

  finishAnimation(element) {
    this.animations.delete(element)
    window.setTimeout(() => element.classList.remove("is-live-changing"), 360)
  }

  format(value, kind) {
    if (kind === "money") {
      const rounded = Math.round(Number(value) * 100) / 100
      return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2, maximumFractionDigits: 2 }).format(rounded)
    }
    return new Intl.NumberFormat("en-CA", { maximumFractionDigits: 0 }).format(Math.round(value))
  }

  numberFromText(value) {
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ""))
    return Number.isFinite(parsed) ? parsed : 0
  }

  sameCalendarDates(payload) {
    const renderedGlanceDate = this.element.querySelector("[data-live-glance-date]")?.dataset.liveGlanceDate
    const weekDates = payload.weekly.rows.map((row) => row.date).sort()
    const renderedWeekDates = Array.from(this.element.querySelectorAll("[data-live-week-date]")).map((row) => row.dataset.liveWeekDate).sort()
    return payload.glance.date === renderedGlanceDate && JSON.stringify(weekDates) === JSON.stringify(renderedWeekDates)
  }

  visibilityChanged() {
    if (document.hidden) {
      this.stopTimer()
      this.abortController?.abort()
      this.updateStatus("Paused")
    } else if (this.enabledValue && navigator.onLine) {
      this.schedule(Date.now() - this.lastFetchedAt >= this.intervalValue ? 0 : this.intervalValue)
    }
  }

  connectionChanged() {
    this.stopTimer()
    if (!navigator.onLine) {
      this.abortController?.abort()
      this.updateStatus("Offline")
    } else if (this.enabledValue && !document.hidden) {
      this.schedule(0)
    }
  }

  pauseForNavigation() {
    this.stopTimer()
    this.abortController?.abort()
  }

  updateStatus(label) {
    const status = this.element.querySelector("[data-live-status]")
    if (status) {
      status.textContent = label
      status.dataset.liveState = label.toLowerCase()
    }
  }

  schedule(delay) {
    this.stopTimer()
    this.timer = window.setTimeout(() => this.refresh(), delay)
  }

  stopTimer() {
    if (this.timer) window.clearTimeout(this.timer)
    this.timer = null
  }
}
